import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppExportRules } from "./AppExportRules";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppContact, AppRow } from "./AppIndex";
import { AppAssistAnswer, AppAssistApi, AppAssistClose } from "./AppAssistApi";
import { AppAssistDone } from "./AppAssistPanel";
import { AppAssistRules } from "./AppAssistRules";
import { IComposerSent } from "./AppComposer";
import { AppOutbox } from "./AppOutbox";
import { AppTaskApi } from "./AppTaskApi";
import { AppTaskRules } from "./AppTaskRules";
import { AppIcons } from "./AppIcons";

// AppTaskNotify - "closing a task is two clicks" (owner, 2026-09-26: "When a task is marked as
// completed - the ai should be notified to write a message about the status change. So the whole
// 'closing a task' becomes two clicks - a. Close the task b. Send the ai-written notification.").
//
// When `/tasks` CONFIRMS a task done (the Tasks tab, the Today strip, the contact page's strip or
// "Mark done" on a bubble), a small "Let Anna know?" card appears: "Writing a short update…", then
// the AI's draft as a bubble with **Send to Anna** / **Edit** / **Skip**. The task stays done
// whichever is pressed; Reopen withdraws the card.
//
// ⛔ DECISION 9: THE SEND IS THE OWNER'S SECOND CLICK ON THE EXACT TEXT SHOWN. The ONLY call to
// `IAppNotifyHost.Send` is in `Act("send")`. An arriving draft (`Take`), a render, the close click
// itself - none of them can reach it. The host's `Send` is `AppComposer.SendText` (the composer of
// the open chat, or one made for that contact): the same `/send` request, validation, bubble and
// one-at-a-time latch as the Send button under a chat. ⛔ Never a second sender.
// ⛔ ONE DRAFT REQUEST PER CLOSE, ONE `/assist` IN FLIGHT FROM HERE: the service allows one question
// per number at a time, so a second close waits its turn instead of drawing a throttle refusal.
// ⭐ THE DRAFT IS WRITTEN FROM THE CHAT, AND SOMETIMES NOT AT ALL (owner refinement, 2026-09-26). The
// ask carries the closed task (`AppAssistClose`); the service asks the model over the same context as
// every panel question (identity, notes, task list, the folded transcript) for the message a person
// would really send - or `notify: false` when the chat shows the contact already knows (the owner told
// them, or they reported it and the owner already answered). Then there is NO card to send: one quiet
// ink line, "No message needed - you already told Anna (14:05).", and "Write one anyway" (asks again
// with `force`). An answer that cannot be read is a yes with no draft: "Couldn't write an update".
// ⚠ LATE ANSWERS ARE DROPPED: Skip, Reopen and navigation (`Clear`) remove the card; an answer finds
// no card with its `Seq` and is ignored (it still frees the one slot).
// ⚠ OFFERED ONLY WHERE AI HELP CAN ANSWER: the hosted page, the OWNER's browser (`/folder` said
// `owner: true`), a 1:1 contact, and an `ai` or `you` task - never a reply owed ("Reply to Anna" done
// means it has been dealt with; announcing that is nonsense). A service without `/assist` (no boolean
// `ok`) hides the card silently - no error, no sentence.
// ⭐ WHERE IT SHOWS (owner, 2026-09-26: "There are now two ai areas - combine the bottom one into the right
// one"): on a contact page the card is an entry in the AI panel (`AppAssistView.Panel` carries the region),
// and closing a task in the open chat opens the panel - a sheet on a phone - to show it. On the Tasks and
// Chats tabs, which have no panel, it stays under the tabs. Only the mount point moved: these rules, this
// state and these buttons are the same wherever it is drawn.
// ⚠ PRIVATE DATA: task labels, questions and drafts are the owner's; nothing here logs them.
// ⚠ SHAPED BY AN INTERFACE (`IAppNotifyHost`), so the suite drives it with a fake page.

/** What the card machinery asks of the page. */
export interface IAppNotifyHost {
    /** The archive proof `/assist` takes (the same as `/send`'s). */
    Phone(): string;
    Key(): string;
    /** POST the body to `/assist`; `Done` answered exactly once - (0, "") for a lost connection. */
    Post(Body: string, Done: AppAssistDone): void;
    /** ⭐ Run `Then` once after `Ms` - a `retry` refusal (the archive still being restored) is asked again. Optional. */
    Later?(Ms: number, Then: () => void): void;
    /**
     * ⛔ THE ONE SENDER (`AppComposer.SendText`) for the chat `Href`: "" when the request went (`Then`
     * is then answered once), else the sentence to show. Called ONLY from a Send click.
     */
    Send(Href: string, Text: string, Then: IComposerSent): string;
    /** Edit: open the chat `Href` with `Text` in its message box. Sends nothing. */
    Edit(Href: string, Text: string): void;
    /**
     * ⭐ THE PERSISTED AI HISTORY (2026-09-26): note what the owner did with the card's draft (`sent`,
     * `edited`, `skipped`) on its item `Id` in the chat `Href`'s `ai-chat.json`. ⛔ It RECORDS; nothing is sent.
     */
    Outcome(Href: string, Id: string, Outcome: string): void;
    /** The clock - "you already told Anna (14:05)" is said in the browser's time. */
    Now(): Date;
    /** A card changed - repaint the card regions on screen. */
    Changed(): void;
    /**
     * ⭐ SEVERAL TABS (2026-09-27): the owner acted on the card for task `Id` (`sent` - the request went,
     * `edited`, `skipped`), so the page tells its other tabs to take their card for it down
     * (`AppTabBus`). ⛔ A notice only; optional so a bare host still fits.
     */
    Acted?(Id: string, Outcome: string): void;
}

/** One "Let <name> know?" card. */
export class AppTaskNotice {
    public Id: string = "";
    public Href: string = "";
    /** The contact's name as the archive has it. */
    public Title: string = "";
    /** What was done, as the task list says it. */
    public What: string = "";
    /** Which ask this card is waiting for - an answer for any other is dropped. */
    public Seq: number = 0;
    /** How many times the card's ask was asked again after a `retry` refusal (`AppTaskNotifier.MaxAgain`). */
    public Tries: number = 0;
    /**
     * `writing`, `ready` (a draft is shown), `sending`, `sent`, `why` (a sentence, no draft), or `told`
     * (the chat shows the contact already knows - no message).
     */
    public Phase: string = "writing";
    public Draft: string = "";
    public Why: string = "";
    /** Why the last Send did not go - under the card; Send can be pressed again. */
    public SendWhy: string = "";
    /** The ask this card sends to `/assist` (kept for Try again): a readable question and the task. */
    public Ask: string = "";
    public Close: AppAssistClose = new AppAssistClose();
    /** `told` only: why no message is needed, and when the contact was told (UTC ISO, or ""). */
    public Reason: string = "";
    public ToldIso: string = "";
    /** The draft's item in the chat's AI history (`ai-chat.json`), "" when it was not saved there. */
    public HistoryId: string = "";
}

export class AppTaskNotifyRules {
    public static readonly Writing: string = "writing";
    public static readonly Ready: string = "ready";
    public static readonly Sending: string = "sending";
    public static readonly Sent: string = "sent";
    public static readonly Said: string = "why";
    public static readonly Told: string = "told";
    /** An answer with no draft is used as the message only when it is at most this long. */
    public static readonly ShortMax: number = 600;
    /** The task's words in the ask are cut to this, so the ask stays under `AppAssistApi.MaxAsk`. */
    public static readonly WhatMax: number = 400;
    /**
     * Only these task sources are announced. ⛔ NEVER `reply`: closing "Reply to Anna" means it has been
     * dealt with, and a message announcing that is nonsense (`AppTaskRules.SourceOf` reads an old `r-`
     * row as `reply` too).
     */
    public static readonly Sources: string[] = ["ai", "you"];

    /**
     * Should closing `Row` offer a card? `Allowed` is the page's gate (hosted, owner's browser);
     * `Who` is the task's contact in the index (null = not found). ⛔ Not a reply owed, not a group,
     * not a task with no valid id or no chat.
     */
    public static Offer = (Row: AppRow, Who: AppContact | null, Allowed: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Allowed !== true || Who === null) { return false; }
            if (AppTaskNotifyRules.Sources.indexOf(AppTaskRules.SourceOf(Row)) < 0) { return false; }
            if (AppTaskApi.IdOk(Row.Id) === false || nz(Row.Href).length === 0) { return false; }
            const Contact: AppContact = Who as AppContact;
            if (Contact.IsGroup === true || AppOutbox.IsJid(Contact.ChatId) === false) { return false; }
            return Contact.ChatId.indexOf("@g.us") < 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.Offer");
            return false;
        }
    };

    /** What was done: "Send the invoice - March", cut to `WhatMax`. */
    public static WhatOf = (Row: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let What: string = nz(AppTaskRules.TitleOf(Row)).trim();
            const Value: string = nz(Row.Value).trim();
            if (Value.length > 0 && Value !== What && nz(Row.Label).length > 0) { What = What + " - " + Value; }
            return What.replace(/\s+/g, " ").substring(0, AppTaskNotifyRules.WhatMax);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.WhatOf");
            return "";
        }
    };

    /** Who the message is to, as the ask names them: the first name, else "them". */
    public static NameFor = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "them"; }
        try {
            if (AppAssistRules.IsRawId(Title) === true) { return "them"; }
            const First: string = AppTaskRules.FirstName(Title);
            if (First.length === 0) { return "them"; }
            return First;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.NameFor");
            return "them";
        }
    };

    /**
     * The ask `/assist` gets. ⭐ WHO OWED IT DECIDES THE TONE: `me` - "I've done it"; `them` -
     * "thanks, noted that you did it"; `both` or not said - neutral. It asks for the message only,
     * after the draft marker, so the answer lands in the draft (the service's prompt already says so
     * for any request for a message; this repeats it for the one ask where nothing else is wanted).
     */
    public static AskFor = (Row: AppRow, Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Name: string = AppTaskNotifyRules.NameFor(Title);
            let Out: string = "Write a short WhatsApp message to " + Name + " about \""
                + AppTaskNotifyRules.WhatOf(Row) + "\" now being done - what was done and any detail from our chat.";
            if (Row.Owner === "me") { Out = Out + " It was mine to do and I've done it - tell them so."; }
            if (Row.Owner === "them") { Out = Out + " It was theirs to do and they did it - thank them and say I've noted it's done."; }
            if (Row.Owner !== "me" && Row.Owner !== "them") { Out = Out + " Keep it neutral - just say it's sorted."; }
            Out = Out + " Write it in my usual style and language, as in this chat, and never say \"task\" or \"marked as"
                + " done\". Only the message, after the " + AppAssistApi.DraftMarker + " line.";
            return Out.substring(0, AppAssistApi.MaxAsk);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.AskFor");
            return "";
        }
    };

    /**
     * The message to show: the draft; else the answer when it is short (a model that forgot the
     * marker); else "" (nothing sendable came back). ⛔ Never longer than one message can be.
     */
    public static DraftFrom = (Answer: AppAssistAnswer): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Answer.Ok !== true) { return ""; }
            let Text: string = nz(Answer.Draft).trim();
            // ⚠ ONLY FROM A SERVICE THAT DOES NOT KNOW THE CLOSED-TASK ASK: a close-aware answer with no
            // draft is "Couldn't write an update", never its explanation dressed up as the message.
            if (Text.length === 0 && Answer.CloseAware !== true && nz(Answer.Answer).trim().length <= AppTaskNotifyRules.ShortMax) {
                Text = nz(Answer.Answer).trim();
            }
            if (Text.length > AppOutbox.MaxText) { return ""; }
            return Text;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.DraftFrom");
            return "";
        }
    };

    /** The closed task as the service reads it (`AssistRules.CloseIn`). */
    public static CloseOf = (Row: AppRow): AppAssistClose => {
        if (getCoreUtils().KErrorMode === true) { return new AppAssistClose(); }
        try {
            const Out: AppAssistClose = new AppAssistClose();
            Out.Id = Row.Id;
            Out.Label = nz(Row.Label).trim();
            Out.Value = nz(Row.Value).trim();
            if (Out.Label.length === 0) { Out.Label = Out.Value; Out.Value = ""; }
            if (["me", "them", "both"].indexOf(Row.Owner) >= 0) { Out.Owner = Row.Owner; }
            Out.Source = AppTaskRules.SourceOf(Row);
            if (AppTaskRules.IsDay(Row.Date) === true) { Out.Date = Row.Date; }
            if (Out.Date.length > 0 && /^\d{2}:\d{2}$/.test(nz(Row.Time)) === true) { Out.Time = Row.Time; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.CloseOf");
            return new AppAssistClose();
        }
    };

    /**
     * "No message needed - you already told Anna (14:05)." The reason is the model's few words (escaped
     * by the view), "them" made the contact's first name; the time is the browser's, with the day when
     * it was not today.
     */
    public static ToldLine = (Notice: AppTaskNotice, Now: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return "No message needed."; }
        try {
            const Name: string = AppTaskNotifyRules.NameFor(Notice.Title);
            let Reason: string = nz(Notice.Reason).trim();
            if (Reason.length === 0) { Reason = "the chat shows they already know"; }
            if (Name !== "them") {
                Reason = Reason.replace(/^you already told them\b/i, "you already told " + Name)
                    .replace(/^they told you\b/i, Name + " told you");
            }
            return "No message needed - " + Reason + AppTaskNotifyRules.WhenOf(Notice.ToldIso, Now) + ".";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.ToldLine");
            return "No message needed.";
        }
    };

    /** " (14:05)" today, " (Fri 25 Sep 14:05)" another day, "" without a readable time. */
    public static WhenOf = (Iso: string, Now: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Ms: number = Date.parse(nz(Iso));
            if (nz(Iso).length === 0 || isNaN(Ms) === true) { return ""; }
            const Offset: number = AppTaskRules.OffsetOf(Now);
            const Local: Date = new Date(Ms + Offset * 60000);
            const Clock: string = ("0" + String(Local.getUTCHours())).slice(-2) + ":" + ("0" + String(Local.getUTCMinutes())).slice(-2);
            const Day: string = AppTaskRules.LocalDay(Ms, Offset);
            if (Day === AppTaskRules.TodayLocal(Now)) { return " (" + Clock + ")"; }
            return " (" + AppTaskRules.DayLabel(Day) + " " + Clock + ")";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.WhenOf");
            return "";
        }
    };

    /** "Let Anna know?" - or "Let them know?" for a name that is an id or a number. */
    public static Heading = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "Let them know?"; }
        try {
            return "Let " + AppTaskNotifyRules.NameFor(Title) + " know?";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.Heading");
            return "Let them know?";
        }
    };

    /**
     * The heading in the AI panel ("Ask about Anna"), where the name is already on screen and the task is
     * not: "Visa documents done – let Anna know?".
     */
    public static ChatHeading = (What: string, Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "Let them know?"; }
        try {
            if (nz(What).trim().length === 0) { return AppTaskNotifyRules.Heading(Title); }
            return nz(What).trim() + " done – let " + AppTaskNotifyRules.NameFor(Title) + " know?";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.ChatHeading");
            return "Let them know?";
        }
    };

    /** Send may be pressed: a draft on screen, within `/send`'s limit, not sending or sent. */
    public static CanSend = (Notice: AppTaskNotice): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Notice.Phase !== AppTaskNotifyRules.Ready) { return false; }
            return Notice.Draft.trim().length > 0 && Notice.Draft.length <= AppOutbox.MaxText;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyRules.CanSend");
            return false;
        }
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static EmptyWhy = (): string => {
        return "Couldn't write an update. Write one yourself with Edit, or skip it.";
    };
}

/** One `/assist` call for a card, carrying the card and the `Seq` it was asked with. */
export class AppTaskNotifyCall {
    private Owner: AppTaskNotifier;
    private Notice: AppTaskNotice;
    private Seq: number;
    private Ended: boolean = false;

    public constructor(Owner: AppTaskNotifier, Notice: AppTaskNotice, Seq: number) {
        this.Owner = Owner;
        this.Notice = Notice;
        this.Seq = Seq;
    }

    /** ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - this frees the one `/assist` slot. */
    public OnDone = (Status: number, Text: string): void => {
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            this.Owner.Take(this.Notice, this.Seq, AppAssistApi.Parse(Status, Text, AppAssistApi.LostWhy()));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyCall.OnDone");
        }
    };
}

/** One scheduled re-ask of a card (a named callback for `IAppNotifyHost.Later`). */
export class AppTaskNotifyAgain {
    private Owner: AppTaskNotifier;
    private Notice: AppTaskNotice;
    private Seq: number;

    public constructor(Owner: AppTaskNotifier, Notice: AppTaskNotice, Seq: number) {
        this.Owner = Owner;
        this.Notice = Notice;
        this.Seq = Seq;
    }

    public Run = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Owner.Again(this.Notice, this.Seq);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyAgain.Run");
        }
    };
}

/** How ONE card's send went (`IComposerSent`), for the card it came from. */
export class AppTaskNotifySend implements IComposerSent {
    private Owner: AppTaskNotifier;
    private Notice: AppTaskNotice;

    public constructor(Owner: AppTaskNotifier, Notice: AppTaskNotice) {
        this.Owner = Owner;
        this.Notice = Notice;
    }

    /** ⚠ ACCEPTED, NOT DELIVERED: "In the chat ✓"; the bubble in the chat says when WhatsApp has it. */
    public Queued = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Notice.Phase = AppTaskNotifyRules.Sent;
            this.Notice.SendWhy = "";
            this.Owner.Noted(this.Notice, "sent");
            this.Owner.Painted(this.Notice);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifySend.Queued");
        }
    };

    public Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Notice.Phase = AppTaskNotifyRules.Ready;
            this.Notice.SendWhy = "Not sent. " + nz(Why);
            this.Owner.Painted(this.Notice);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifySend.Failed");
        }
    };
}

export class AppTaskNotifier {
    private Host: IAppNotifyHost;
    /** The cards on offer now, oldest first. */
    public Cards: AppTaskNotice[] = [];
    /** Cards waiting for their turn at `/assist`. */
    private Queue: AppTaskNotice[] = [];
    /** One `/assist` call from here at a time. */
    private InFlight: boolean = false;
    private Seq: number = 0;

    public constructor(Host: IAppNotifyHost) {
        this.Host = Host;
    }

    /**
     * `/tasks` confirmed `Row` done. Offers a card when `AppTaskNotifyRules.Offer` says so, and asks
     * for its draft - once. Answers true when a NEW card was offered (the page then shows the panel).
     * ⛔ THIS SENDS NOTHING: it asks the AI to write, and that is all.
     */
    public Closed = (Row: AppRow, Who: AppContact | null, Allowed: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppTaskNotifyRules.Offer(Row, Who, Allowed) === false) { return false; }
            if (this.Find(Row.Id) !== null) { return false; }
            const Notice: AppTaskNotice = new AppTaskNotice();
            Notice.Id = Row.Id;
            Notice.Href = Row.Href;
            Notice.Title = (Who as AppContact).Title;
            Notice.What = AppTaskNotifyRules.WhatOf(Row);
            Notice.Ask = AppTaskNotifyRules.AskFor(Row, Notice.Title);
            Notice.Close = AppTaskNotifyRules.CloseOf(Row);
            this.Cards.push(Notice);
            this.Enqueue(Notice);
            this.Host.Changed();
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Closed");
            return false;
        }
    };

    /** The task was reopened: its card goes (a late answer is then dropped). */
    public Reopened = (Id: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Notice: AppTaskNotice | null = this.Find(Id);
            if (Notice === null) { return; }
            this.Drop(Notice as AppTaskNotice);
            this.Host.Changed();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Reopened");
        }
    };

    /** The other tabs hear that the card for `Id` was acted on (`IAppNotifyHost.Acted`) - a notice only. */
    private TellActed = (Id: string, Outcome: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Host.Acted !== undefined) { this.Host.Acted(Id, Outcome); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.TellActed");
        }
    };

    /** The owner went elsewhere: every card goes, and every late answer is dropped. */
    public Clear = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Cards = [];
            this.Queue = [];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Clear");
        }
    };

    /** The cards for the chat `Href`, or every card for "". */
    public For = (Href: string): AppTaskNotice[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppTaskNotice[] = [];
            for (const One of this.Cards) {
                if (nz(Href).length === 0 || One.Href === Href) { Out.push(One); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.For");
            return [];
        }
    };

    /**
     * A card's button was pressed: `send`, `edit`, `skip` or `again`.
     * ⛔ `send` IS THE ONLY WAY A CARD'S TEXT LEAVES - see the header.
     */
    public Act = (Id: string, Name: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Notice: AppTaskNotice | null = this.Find(Id);
            if (Notice === null) { return; }
            const One: AppTaskNotice = Notice as AppTaskNotice;
            if (Name === "send") { this.SendOne(One); return; }
            if (Name === "skip") {
                if (One.Phase === AppTaskNotifyRules.Ready) { this.Noted(One, "skipped"); }
                this.Drop(One);
                this.Host.Changed();
                this.TellActed(One.Id, "skipped");
                return;
            }
            if (Name === "edit") {
                // ⚠ INTO THE BOX, NEVER OUT OF IT: the chat opens with the draft; the owner presses Send.
                this.Noted(One, "edited");
                this.Drop(One);
                this.Host.Changed();
                this.TellActed(One.Id, "edited");
                this.Host.Edit(One.Href, One.Draft);
                return;
            }
            if (Name === "again" && One.Phase === AppTaskNotifyRules.Said) { this.Enqueue(One); this.Host.Changed(); return; }
            // ⭐ "WRITE ONE ANYWAY": the owner overrides "no message needed" - asked again with `force`.
            if (Name === "anyway" && One.Phase === AppTaskNotifyRules.Told) {
                One.Close.Force = true;
                this.Enqueue(One);
                this.Host.Changed();
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Act");
        }
    };

    /**
     * The card's Send. ⛔ MARKED `sending` BEFORE THE HOST IS CALLED, so a second click finds
     * `CanSend` false - one click, one message.
     */
    private SendOne = (Notice: AppTaskNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppTaskNotifyRules.CanSend(Notice) !== true) { return; }
            Notice.Phase = AppTaskNotifyRules.Sending;
            Notice.SendWhy = "";
            this.Host.Changed();
            const Why: string = this.Host.Send(Notice.Href, Notice.Draft, new AppTaskNotifySend(this, Notice));
            if (Why.length > 0) {
                Notice.Phase = AppTaskNotifyRules.Ready;
                Notice.SendWhy = Why;
                this.Host.Changed();
                return;
            }
            this.TellActed(Notice.Id, "sent");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.SendOne");
        }
    };

    /**
     * An answer for `Notice` asked with `Seq`. ⛔ IT ONLY RECORDS THE DRAFT - it never sends.
     * Dropped when the card went (Skip, Reopen, navigation) or was asked again since.
     */
    public Take = (Notice: AppTaskNotice, Seq: number, Answer: AppAssistAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.InFlight = false;
            // ⭐ THE RESTART WINDOW (2026-09-29): the card keeps "Writing a short update…" and asks again - never the final
            // "not built" sentence. After `MaxAgain` the service's sentence is shown as before.
            const Wait: number = AppExportRules.AgainAfterMs(Answer, Notice.Tries);
            if (this.Cards.indexOf(Notice) >= 0 && Notice.Seq === Seq && Notice.Phase === AppTaskNotifyRules.Writing
                && Wait > 0 && this.Host.Later !== undefined) {
                Notice.Tries = Notice.Tries + 1;
                this.Host.Later(Wait, new AppTaskNotifyAgain(this, Notice, Seq).Run);
                this.Pump();
                return;
            }
            if (this.Cards.indexOf(Notice) >= 0 && Notice.Seq === Seq && Notice.Phase === AppTaskNotifyRules.Writing) {
                this.Settle(Notice, Answer);
                this.Host.Changed();
            }
            this.Pump();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Take");
        }
    };

    private Settle = (Notice: AppTaskNotice, Answer: AppAssistAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⚠ NO ROUTE = NO AI HELP ON THIS SERVICE: the card simply goes - no error, no sentence.
            if (Answer.NoRoute === true) { this.Drop(Notice); return; }
            if (Answer.Ok !== true) { Notice.Phase = AppTaskNotifyRules.Said; Notice.Why = nz(Answer.Why); return; }
            // ⭐ THE CHAT SHOWS THEY ALREADY KNOW: no card to send - one quiet line (unless forced).
            if (Answer.CloseAware === true && Answer.Notify === false && Notice.Close.Force !== true) {
                Notice.Phase = AppTaskNotifyRules.Told;
                Notice.Reason = nz(Answer.Reason);
                Notice.ToldIso = nz(Answer.ToldIso);
                Notice.Draft = "";
                return;
            }
            const Draft: string = AppTaskNotifyRules.DraftFrom(Answer);
            if (Draft.length === 0) { Notice.Phase = AppTaskNotifyRules.Said; Notice.Why = AppTaskNotifyRules.EmptyWhy(); return; }
            Notice.Draft = Draft;
            Notice.Phase = AppTaskNotifyRules.Ready;
            if (Answer.Saved === true) { Notice.HistoryId = nz(Answer.DraftId); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Settle");
        }
    };

    /**
     * Note what became of the card's draft in the chat's AI history - once per card, and only when the draft
     * was saved there. ⛔ It records; the send (if any) already went through the composer.
     */
    public Noted = (Notice: AppTaskNotice, Outcome: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Notice.HistoryId.length === 0) { return; }
            const Id: string = Notice.HistoryId;
            Notice.HistoryId = "";
            this.Host.Outcome(Notice.Href, Id, Outcome);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Noted");
        }
    };

    /** A card's send answered - repaint if it is still on offer. */
    public Painted = (Notice: AppTaskNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Cards.indexOf(Notice) < 0) { return; }
            this.Host.Changed();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Painted");
        }
    };

    private Enqueue = (Notice: AppTaskNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Seq = this.Seq + 1;
            Notice.Seq = this.Seq;
            Notice.Phase = AppTaskNotifyRules.Writing;
            Notice.Why = "";
            this.Queue.push(Notice);
            this.Pump();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Enqueue");
        }
    };

    /** Ask for the next waiting card's draft, when nothing from here is at `/assist`. */
    /** Quiet re-asks of one card after `retry` refusals (15 s each at the service = 5 minutes). */
    public static readonly MaxAgain: number = AppExportRules.MaxAgain;

    /** A card's ask, again - only while it is still that card, still waiting for that ask. */
    public Again = (Notice: AppTaskNotice, Seq: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Cards.indexOf(Notice) < 0 || Notice.Seq !== Seq || Notice.Phase !== AppTaskNotifyRules.Writing) { return; }
            this.Queue.push(Notice);
            this.Pump();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Again");
        }
    };

    private Pump = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            while (this.InFlight === false && this.Queue.length > 0) {
                const Next: AppTaskNotice = this.Queue.shift() as AppTaskNotice;
                if (this.Cards.indexOf(Next) < 0 || Next.Phase !== AppTaskNotifyRules.Writing) { continue; }
                const Call: AppTaskNotifyCall = new AppTaskNotifyCall(this, Next, Next.Seq);
                const Body: string = AppAssistApi.CloseBody(this.Host.Phone(), this.Host.Key(), Next.Href, Next.Ask, Next.Close);
                this.InFlight = true;
                // ⚠ SIGNED OUT: nothing to ask with - the card goes, like a service without the route.
                if (Body.length === 0) { Call.OnDone(200, "{}"); continue; }
                this.Host.Post(Body, Call.OnDone);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Pump");
        }
    };

    private Find = (Id: string): AppTaskNotice | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (const One of this.Cards) {
                if (One.Id === Id) { return One; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Find");
            return null;
        }
    };

    private Drop = (Notice: AppTaskNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const At: number = this.Cards.indexOf(Notice);
            if (At >= 0) { this.Cards.splice(At, 1); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifier.Drop");
        }
    };
}

/**
 * The cards' markup. ⛔ EVERY WORD FROM A CONVERSATION OR THE MODEL IS ESCAPED (`kHTMLCore.EscapeHtml`) -
 * the draft is the model's text, the task and the name are the archive's.
 */
export class AppTaskNotifyView {
    public static readonly RegionId: string = "wac-tnotify";
    public static readonly ActAttr: string = "data-wac-nact";
    public static readonly IdAttr: string = "data-wac-nid";

    /** The quiet "no message needed" line - no send, no draft; "Write one anyway" and a dismiss. */
    public static Told = (One: AppTaskNotice, Now: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<div class=\"tnote tnquiet p-told\" " + AppTaskNotifyView.IdAttr + "=\"" + kHTMLCore.EscapeAttr(One.Id)
                + "\"><p class=\"tntold\"><span class=\"tnok\" aria-hidden=\"true\">" + AppIcons.Svg(AppIcons.Check, 16) + "</span>"
                + kHTMLCore.EscapeHtml(AppTaskNotifyRules.ToldLine(One, Now)) + " "
                + AppTaskNotifyView.Button(One, "anyway", "tnlink btn ghost sm", "Write one anyway", false)
                + AppTaskNotifyView.Button(One, "skip", "tnx btn ghost icon sm", AppIcons.Svg(AppIcons.Close, 16), false, "Dismiss")
                + "</p></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyView.Told");
            return "";
        }
    };

    /** The empty region a view carries; `Cards` fills it. */
    public static Region = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<div class=\"tnotify\" id=\"" + AppTaskNotifyView.RegionId + "\" aria-live=\"polite\"></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyView.Region");
            return "";
        }
    };

    /** `InPanel`: the region is the one in the AI panel on a contact page (the "<task> done – let …" heading). */
    public static Cards = (List: AppTaskNotice[], Now: Date, InPanel: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [];
            for (const One of List) {
                if (One.Phase === AppTaskNotifyRules.Told) { Out.push(AppTaskNotifyView.Told(One, Now)); continue; }
                Out.push(AppTaskNotifyView.Card(One, InPanel));
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyView.Cards");
            return "";
        }
    };

    /** One card: the heading, then the state - writing, the draft bubble, or a sentence - then its buttons. */
    public static Card = (One: AppTaskNotice, InPanel: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Head: string = "<b>" + kHTMLCore.EscapeHtml(AppTaskNotifyRules.Heading(One.Title)) + "</b> <span class=\"tnwhat\">&#8220;"
                + kHTMLCore.EscapeHtml(One.What) + "&#8221; is done.</span>";
            // ⚠ In the panel the heading carries the task's LABEL alone (the value made it a sentence too long
            // for a phone line); a card with no label falls back to what the task list says.
            let Short: string = nz(One.Close.Label);
            if (Short.trim().length === 0) { Short = One.What; }
            if (InPanel === true) { Head = "<b>" + kHTMLCore.EscapeHtml(AppTaskNotifyRules.ChatHeading(Short, One.Title)) + "</b>"; }
            const Out: string[] = ["<div class=\"tnote p-" + kHTMLCore.EscapeAttr(One.Phase) + "\" "
                + AppTaskNotifyView.IdAttr + "=\"" + kHTMLCore.EscapeAttr(One.Id) + "\">"
                + "<p class=\"tnhead\"><span class=\"spark\" aria-hidden=\"true\">" + AppIcons.Svg(AppIcons.Spark, 14) + "</span>"
                + Head + "</p>"];
            if (One.Phase === AppTaskNotifyRules.Writing) {
                Out.push("<p class=\"tnwait\" role=\"status\"><span class=\"adots\" aria-hidden=\"true\"></span>"
                    + "Writing a short update&#8230;</p>");
            }
            if (One.Phase === AppTaskNotifyRules.Said) { Out.push("<p class=\"tnwhy\">" + kHTMLCore.EscapeHtml(One.Why) + "</p>"); }
            if (One.Draft.length > 0 && One.Phase !== AppTaskNotifyRules.Said && One.Phase !== AppTaskNotifyRules.Writing) {
                Out.push("<div class=\"tnbubble\">" + kHTMLCore.EscapeHtml(One.Draft) + "</div>");
            }
            Out.push(AppTaskNotifyView.Buttons(One));
            if (One.SendWhy.length > 0) { Out.push("<p class=\"tnwhy\">" + kHTMLCore.EscapeHtml(One.SendWhy) + "</p>"); }
            if (One.Phase === AppTaskNotifyRules.Sent) {
                Out.push("<p class=\"tnsent\">Added to the chat - its bubble shows when WhatsApp has it.</p>");
            }
            Out.push("</div>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyView.Card");
            return "";
        }
    };

    /**
     * ⭐ EVERY CARD WITH A DRAFT HAS SKIP, EDIT AND SEND; a card still writing has Skip; a sentence has
     * Skip, Edit (write it yourself) and Try again; a sent card has Close.
     */
    private static Buttons = (One: AppTaskNotice): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<div class=\"tnacts\">"];
            if (One.Phase === AppTaskNotifyRules.Sent) {
                Out.push(AppTaskNotifyView.Button(One, "skip", "abtn btn secondary sm", "Close", false));
                Out.push(AppTaskNotifyView.Button(One, "send", "abtn ause btn send sm", "In the chat" + AppIcons.Svg(AppIcons.Check, 16), true));
                return Out.join("") + "</div>";
            }
            Out.push(AppTaskNotifyView.Button(One, "skip", "abtn btn secondary sm", "Skip", false));
            if (One.Phase === AppTaskNotifyRules.Writing) { return Out.join("") + "</div>"; }
            Out.push(AppTaskNotifyView.Button(One, "edit", "abtn btn secondary sm", "Edit", One.Phase === AppTaskNotifyRules.Sending));
            if (One.Phase === AppTaskNotifyRules.Said) {
                Out.push(AppTaskNotifyView.Button(One, "again", "abtn btn secondary sm", "Try again", false));
                return Out.join("") + "</div>";
            }
            let Label: string = kHTMLCore.EscapeHtml(AppAssistRules.SendLabel(One.Title));
            if (One.Phase === AppTaskNotifyRules.Sending) { Label = "Sending&#8230;"; }
            Out.push(AppTaskNotifyView.Button(One, "send", "abtn ause btn send sm", Label, AppTaskNotifyRules.CanSend(One) !== true));
            return Out.join("") + "</div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyView.Buttons");
            return "";
        }
    };

    /** `Label` is markup already escaped by the caller (or a constant with an entity). */
    private static Button = (One: AppTaskNotice, Act: string, Cls: string, Label: string, Off: boolean,
                             Aria: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Disabled: string = "";
            if (Off === true) { Disabled = " disabled"; }
            if (Aria.length > 0) { Disabled = Disabled + " aria-label=\"" + kHTMLCore.EscapeAttr(Aria) + "\" title=\""
                + kHTMLCore.EscapeAttr(Aria) + "\""; }
            return "<button type=\"button\" class=\"" + Cls + "\" " + AppTaskNotifyView.ActAttr + "=\"" + Act + "\" "
                + AppTaskNotifyView.IdAttr + "=\"" + kHTMLCore.EscapeAttr(One.Id) + "\"" + Disabled + ">" + Label + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskNotifyView.Button");
            return "";
        }
    };
}
